import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Link as RouterLink } from "react-router-dom";

import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import {
  filterMessageTemplatesByOrganization,
  formatTemplateDisplayName,
  isMessageTemplateListedForSend,
} from "../../messageTemplates/utils/messageTemplateUiUtils";
import { doesTemplateRequireQuestionnaire } from "../helpers/templateQuestionnaireHelpers";
import { isVetApp } from "../helpers/isVetApp";
import { usePatientLookup } from "../hooks/usePatientLookup";
import { useTutorLookup } from "../hooks/useTutorLookup";
import { useSendWhatsAppMessage } from "../hooks/useSendWhatsAppMessage";
import {
  resolveOptionalAppointmentBasedOn,
  useQuestionnaireTaskSend,
} from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import type { QuestionnairePayload } from "src-next/modules/questionnaire-builder/form-builder/types";
import { normalizeCpf } from "src-next/shared/utils/cpf";
import { useInternationalVerify } from "@/utils/useInternationalVerify";
import { usePostAttendanceContext } from "@/contexts/PostAttendanceContext";
import { oids } from "@/configs/Settings";
import { PatientSelectionList } from "./PatientSelectionList";
import { TutorSelectionList } from "./TutorSelectionList";
import type {
  MessagingAppointment,
  MessagingPatient,
  MessagingQuestionnaireOption,
  MessagingTemplate,
} from "../types";
import { CircularLoading } from "@/components/common";
import CommunicationsTablePagination from "src-next/modules/crm-clinical/communications/components/CommunicationsTablePagination";
import {
  communicationsRankFilledSearchFieldSx,
  communicationsRankRowSx,
  communicationsRankTableToolbarSx,
} from "src-next/modules/crm-clinical/communications/components/communicationsSubsectionStyles";
import { useTablePagination } from "src-next/modules/crm-clinical/communications/hooks/useTablePagination";
import CrmSetupContractFluxmedPackagesSection from "../../setup/components/steps/CrmSetupContractFluxmedPackagesSection";
import { useCrmSendMessageFranchiseGate } from "../hooks/useCrmSendMessageFranchiseGate";

type DialogView = "patient" | "template";

/** Altura fixa da lista de modelos — evita o modal “pular” ao filtrar/paginar. */
const TEMPLATE_MODELS_PANEL_HEIGHT_PX = 380;

const fluxZapLinkSx = {
  color: "primary.main",
  fontWeight: 600,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
} as const;

function resolvePatientReferenceForQuestionnaireTask(
  explicitPatientReference: string | null | undefined,
  recipient: MessagingPatient,
): string | undefined {
  const explicit = String(explicitPatientReference ?? "").trim();
  if (explicit) return explicit;

  // Tutor (vet): Task/questionário no app do titular — Patient lógico por CPF, não RelatedPerson nem pet.
  if (recipient.isTutor) {
    const raw = String(recipient.cpf ?? "").trim();
    if (!raw) return undefined;
    const digits = normalizeCpf(raw);
    if (digits.length !== 11) return undefined;
    return `Patient/${oids.cpf}-${digits}`;
  }

  const id = String(recipient.id ?? "").trim();
  if (!id) return undefined;
  const lower = id.toLowerCase();
  if (lower.startsWith("dashboard-") || lower.startsWith("temp-")) {
    return undefined;
  }
  // Agenda vet (legado): id `tutor-of-{petPatientId}` + CPF do tutor nos props.
  // Task/questionário devem ir para o Patient lógico do tutor (CPF), não o pet.
  if (id.startsWith("tutor-of-")) {
    const raw = String(recipient.cpf ?? "").trim();
    const digits = raw ? normalizeCpf(raw) : "";
    if (digits.length === 11) {
      return `Patient/${oids.cpf}-${digits}`;
    }
    return undefined;
  }
  if (id.startsWith("Patient/")) return id;
  return `Patient/${id}`;
}

export interface SendWhatsAppMessageDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Appointment é sempre injetado pelo chamador.
   * O Dialog não seleciona appointment.
   */
  appointment: MessagingAppointment;
  /**
   * Quando informado, o Dialog não pede seleção de paciente.
   */
  patient?: MessagingPatient | null;
  /**
   * Quando informado, o template já inicia selecionado.
   */
  template?: MessagingTemplate | null;
  /**
   * Lista de templates disponíveis para seleção.
   */
  templates: MessagingTemplate[];
  /**
   * Lista de questionários disponíveis quando o template exige questionário.
   */
  questionnaires?: MessagingQuestionnaireOption[];
  isQuestionnairesLoading?: boolean;
  questionnairesErrorMessage?: string | null;
  /**
   * Organização (tenant) para resolver o Endpoint FHIR / WABA rank 1.
   */
  organizationId?: string;
  /**
   * Referência FHIR do solicitante para o FluxZap (ex.: `PractitionerRole/{id}`).
   * Se omitido, usa `VITE_FLUXZAP_REQUESTER_REFERENCE` quando definido.
   */
  practitionerId?: string | null;
  /**
   * Callback executado após envio bem-sucedido (ex.: atualizar estatísticas de franquia).
   */
  onMessageSent?: () => void;
  /**
   * Quando definido, a etapa de seleção usa esta lista (filtro/busca locais no
   * `PatientSelectionList`) em vez de buscar pacientes via `usePatientLookup`.
   * Útil para CRM / listas já filtradas no contexto da tela.
   */
  staticPatientListForSelection?: MessagingPatient[];
  /**
   * Pré-seleciona destinatários ao abrir (ex.: lista CRM com seleção múltipla).
   * Ignorado quando `patient` está definido (fluxo de um único destinatário).
   */
  initialSelectedPatients?: MessagingPatient[];
  /**
   * `Patient/{id}` usado para criar Task de questionário após o FluxZap (mesmo alvo
   * do restante do fluxo de questionário). Omitir quando não houver id de paciente FHIR
   * (não cria task; envio de WhatsApp segue normal).
   */
  taskPatientReference?: string | null;
  /**
   * Quando `false`, desliga verificação de saldo mesmo com `organizationId`.
   * Omitido: verifica automaticamente se `organizationId` estiver definido (agenda, CRM, etc.).
   */
  enforceMessageFranchiseGate?: boolean;
  /**
   * Incrementar após envio/compra para recarregar métricas de franquia (board CRM).
   */
  franchiseRefreshSignal?: number;
  /** Canal Meta já resolvido pelo board CRM — evita race no gate. */
  hasActiveMetaEndpoint?: boolean;
  /**
   * Modo exclusivo de compra de pacotes CRM: exibe apenas o aviso de saldo zerado
   * e a seção de contratação — sem paciente, template ou fluxo de envio.
   */
  purchasePackagesOnly?: boolean;
}

/**
 * SendWhatsAppMessageDialog
 *
 * Dialog de domínio para envio de mensagens via WhatsApp.
 * - Segue o padrão visual/estrutural do dialog do Dashboard (Title/Content dividers/Actions).
 * - Mantém UI "dumb": orquestração e IO ficam nos hooks/services do módulo.
 */
export const SendWhatsAppMessageDialog = memo(
  ({
    open,
    onClose,
    appointment,
    patient: patientFromProps,
    template: templateFromProps,
    templates,
    questionnaires = [],
    isQuestionnairesLoading = false,
    questionnairesErrorMessage = null,
    organizationId,
    practitionerId,
    onMessageSent,
    taskPatientReference,
    staticPatientListForSelection,
    initialSelectedPatients,
    enforceMessageFranchiseGate,
    franchiseRefreshSignal = 0,
    hasActiveMetaEndpoint,
    purchasePackagesOnly = false,
  }: SendWhatsAppMessageDialogProps) => {
    const theme = useTheme();
    const { contractPostAttendance } = usePostAttendanceContext();
    const hasFluxZapExtensionActive = Boolean(contractPostAttendance?.id);
    const vetApp = isVetApp();

    const franchiseGate = useCrmSendMessageFranchiseGate({
      organizationId,
      enforceMessageFranchiseGate: purchasePackagesOnly
        ? false
        : enforceMessageFranchiseGate,
      franchiseRefreshSignal,
      hasActiveMetaEndpoint,
      dialogOpen: open && !purchasePackagesOnly,
    });

    /** CRM / caller já definiu destinatários — pula a etapa de seleção de paciente. */
    const skipPatientSelectionStep = useMemo(() => {
      if (patientFromProps) return true;
      return (initialSelectedPatients?.length ?? 0) > 0;
    }, [patientFromProps, initialSelectedPatients]);

    const [activeView, setActiveView] = useState<DialogView>(() =>
      skipPatientSelectionStep ? "template" : "patient",
    );

    const [selectedPatients, setSelectedPatients] = useState<
      MessagingPatient[]
    >(patientFromProps ? [patientFromProps] : []);
    const [selectedTutors, setSelectedTutors] = useState<MessagingPatient[]>(
      [],
    );
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
      templateFromProps?.id ?? null,
    );
    const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
      string | null
    >(null);
    const [templateSearchQuery, setTemplateSearchQuery] = useState("");

    const templatesScopedForOrganization = useMemo(() => {
      const scoped = filterMessageTemplatesByOrganization(
        templates ?? [],
        organizationId,
      );
      return scoped.filter((template) =>
        isMessageTemplateListedForSend(template.status),
      );
    }, [organizationId, templates]);

    const templatesForSelection = useMemo(() => {
      if (templateFromProps) {
        return [templateFromProps];
      }
      return templatesScopedForOrganization;
    }, [templateFromProps, templatesScopedForOrganization]);

    const filteredTemplatesForSelection = useMemo(() => {
      if (templateFromProps) return templatesForSelection;
      const q = templateSearchQuery.trim().toLowerCase();
      if (!q) return templatesForSelection;
      return templatesForSelection.filter((template) => {
        const name = formatTemplateDisplayName(template.name, {
          organizationPrefix: organizationId,
        }).toLowerCase();
        const body = `${template.description ?? ""} ${template.preview ?? ""}`
          .trim()
          .toLowerCase();
        return name.includes(q) || body.includes(q);
      });
    }, [
      organizationId,
      templateFromProps,
      templateSearchQuery,
      templatesForSelection,
    ]);

    const templateListResetKey = useMemo(
      () => `${filteredTemplatesForSelection.length}-${templateSearchQuery}`,
      [filteredTemplatesForSelection.length, templateSearchQuery],
    );

    const {
      paginatedItems: paginatedTemplates,
      page: templatePage,
      rowsPerPage: templateRowsPerPage,
      totalCount: templateListCount,
      handlePageChange: handleTemplatePageChange,
      handleRowsPerPageChange: handleTemplateRowsPerPageChange,
    } = useTablePagination(
      filteredTemplatesForSelection,
      templateListResetKey,
      {
        defaultRowsPerPage: 10,
      },
    );

    const selectedTemplate = useMemo(() => {
      if (!selectedTemplateId) return null;
      return (
        templatesForSelection.find(
          (template) => template.id === selectedTemplateId,
        ) ?? null
      );
    }, [selectedTemplateId, templatesForSelection]);

    const templateRequiresQuestionnaire = useMemo(
      () => doesTemplateRequireQuestionnaire(selectedTemplate),
      [selectedTemplate],
    );

    const selectedQuestionnaire = useMemo(() => {
      if (!selectedQuestionnaireId) return null;
      return (
        questionnaires.find(
          (questionnaire) => questionnaire.id === selectedQuestionnaireId,
        ) ?? null
      );
    }, [questionnaires, selectedQuestionnaireId]);

    const patientLookup = usePatientLookup({ organizationId });
    const tutorLookup = useTutorLookup({ organizationId });

    const sendFlow = useSendWhatsAppMessage({ organizationId, onMessageSent });
    const { sendQuestionnaireTasks } = useQuestionnaireTaskSend();

    /** crm20 → crm50/crm100: compra só após carregar saldo e confirmar zero. */
    const showFranchisePurchaseOnlyView =
      purchasePackagesOnly || franchiseGate.showFranchisePurchaseOnlyView;

    const isDialogContentLoading = useMemo(() => {
      if (purchasePackagesOnly) return false;
      if (franchiseGate.isFranchiseCheckActive && franchiseGate.isGateLoading) {
        return true;
      }
      if (showFranchisePurchaseOnlyView) return false;
      if (sendFlow.isEndpointLoading) return true;
      if (templateRequiresQuestionnaire && isQuestionnairesLoading) return true;
      return false;
    }, [
      franchiseGate.isFranchiseCheckActive,
      franchiseGate.isGateLoading,
      isQuestionnairesLoading,
      purchasePackagesOnly,
      sendFlow.isEndpointLoading,
      showFranchisePurchaseOnlyView,
      templateRequiresQuestionnaire,
    ]);

    const handleFranchisePurchaseDone = useCallback(() => {
      if (!purchasePackagesOnly) {
        franchiseGate.handlePurchaseCompleted();
      }
      onMessageSent?.();
    }, [franchiseGate, onMessageSent, purchasePackagesOnly]);

    useEffect(() => {
      if (!open) return;
      // Quando abre: garante view correta conforme props e reseta seleção de questionário.
      const initialPatients = patientFromProps
        ? [patientFromProps]
        : (initialSelectedPatients ?? []);
      setActiveView(skipPatientSelectionStep ? "template" : "patient");
      setSelectedPatients(initialPatients);
      setSelectedTutors([]);
      setSelectedTemplateId(templateFromProps?.id ?? null);
      setSelectedQuestionnaireId(null);
      setTemplateSearchQuery("");
    }, [
      open,
      patientFromProps,
      templateFromProps,
      initialSelectedPatients,
      skipPatientSelectionStep,
    ]);

    useEffect(() => {
      if (!open) return;
      if (showFranchisePurchaseOnlyView) return;
      // Lista fixa (CRM, etc.): não dispara busca FHIR/simulada no lookup.
      if (staticPatientListForSelection != null) return;
      // Carrega lista inicial de pacientes somente quando precisa.
      if (patientFromProps) return;
      if ((initialSelectedPatients?.length ?? 0) > 0) return;
      if (vetApp) {
        tutorLookup.load();
      } else {
        patientLookup.search({ term: "" });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      open,
      patientFromProps,
      vetApp,
      staticPatientListForSelection,
      initialSelectedPatients,
      showFranchisePurchaseOnlyView,
    ]);

    const handleClose = useCallback(() => {
      if (sendFlow.isSending || sendFlow.isSendingMany) return;
      onClose();
    }, [onClose, sendFlow.isSending, sendFlow.isSendingMany]);

    const hasFluxzapRequester = useMemo(() => {
      const fromProps = String(practitionerId ?? "").trim();
      const fromEnv = String(
        import.meta.env.VITE_FLUXZAP_REQUESTER_REFERENCE ?? "",
      ).trim();
      return Boolean(fromProps || fromEnv);
    }, [practitionerId]);

    const selectedRecipients = useMemo(() => {
      /**
       * Regra:
       * - No modo humano: sempre envia para `selectedPatients`.
       * - No modo vet: normalmente envia para `selectedTutors` (seleção do dialog).
       *   Porém, quando o caller injeta `patientFromProps` (ex.: agenda/patient search),
       *   precisamos aceitar envio direto com o destinatário já conhecido.
       */
      if (!vetApp) return selectedPatients;
      if (selectedTutors.length > 0) return selectedTutors;
      return selectedPatients;
    }, [selectedPatients, selectedTutors, vetApp]);

    const selectedRecipientsExceedRemaining = useMemo(() => {
      if (!franchiseGate.requiresGate || !franchiseGate.hasMessagesForSend) {
        return false;
      }
      return selectedRecipients.length > franchiseGate.remainingMessages;
    }, [
      franchiseGate.hasMessagesForSend,
      franchiseGate.remainingMessages,
      franchiseGate.requiresGate,
      selectedRecipients.length,
    ]);

    const canContinueToTemplate = useMemo(() => {
      if (selectedRecipients.length === 0) return false;
      if (showFranchisePurchaseOnlyView) return false;
      if (selectedRecipientsExceedRemaining) return false;
      return true;
    }, [
      selectedRecipients.length,
      selectedRecipientsExceedRemaining,
      showFranchisePurchaseOnlyView,
    ]);

    const allSelectedRecipientsAreSendable = useMemo(() => {
      if (selectedRecipients.length === 0) return false;
      if (vetApp) {
        // Modo vet: FluxZap exige CPF do tutor (Patient lógico); RelatedPerson/{id} não é destino válido.
        return selectedRecipients.every(
          (recipient) =>
            normalizeCpf(String(recipient.cpf ?? "").trim()).length === 11,
        );
      }
      return selectedRecipients.every((recipient) =>
        Boolean(String(recipient.cpf ?? "").trim()),
      );
    }, [selectedRecipients, vetApp]);

    const canOpenWhatsAppWeb = useMemo(() => {
      if (selectedRecipients.length !== 1) return false;
      const recipientPhone = String(selectedRecipients[0]?.phone ?? "").trim();
      return Boolean(recipientPhone);
    }, [selectedRecipients]);

    const handleOpenWhatsAppWeb = useCallback(() => {
      if (selectedRecipients.length !== 1) return;
      const phone = String(selectedRecipients[0]?.phone ?? "").trim();
      if (!phone) return;

      window.open(
        `https://web.whatsapp.com/send?phone=+${useInternationalVerify.verifyPhoneDDI(
          phone,
        )}`,
        "_blank",
      );
    }, [selectedRecipients]);

    const canSend = useMemo(() => {
      if (!hasFluxZapExtensionActive) return false;
      if (showFranchisePurchaseOnlyView) return false;
      if (selectedRecipientsExceedRemaining) return false;
      if (selectedRecipients.length === 0) return false;
      if (!selectedTemplate) return false;
      if (templateRequiresQuestionnaire && !selectedQuestionnaire) return false;
      if (!hasFluxzapRequester) return false;
      if (!allSelectedRecipientsAreSendable) return false;
      if (franchiseGate.requiresGate && !franchiseGate.hasMessagesForSend) {
        return false;
      }
      return true;
    }, [
      allSelectedRecipientsAreSendable,
      franchiseGate.hasMessagesForSend,
      franchiseGate.requiresGate,
      hasFluxZapExtensionActive,
      hasFluxzapRequester,
      selectedQuestionnaire,
      selectedRecipientsExceedRemaining,
      selectedRecipients.length,
      selectedTemplate,
      showFranchisePurchaseOnlyView,
      templateRequiresQuestionnaire,
    ]);

    const handleSend = useCallback(async () => {
      if (selectedRecipients.length === 0 || !selectedTemplate) return;

      const result = await sendFlow.sendMany({
        patients: selectedRecipients,
        appointment,
        template: selectedTemplate,
        questionnaire: selectedQuestionnaire,
        /**
         * Injeta explicitamente o identificador preferencial para envio (Meta phone-number-id).
         *
         * Motivação:
         * - O hook já resolve via Endpoint, porém este override garante que o envio unitário/lote
         *   use o valor correto no path do FluxZap no exato momento do disparo.
         * - Mantemos fallback interno para `wabaId` enquanto nem todos os tenants estiverem
         *   com o Endpoint populado com `whatsapp-phone-number-id`.
         */
        phoneNumberId: sendFlow.phoneNumberIdRank1,
        requesterReference: `PractitionerRole/${organizationId}-${practitionerId}`,
      });

      if (result.status === "success") {
        let taskCreatesOk = 0;
        let taskCreatesFail = 0;
        if (
          doesTemplateRequireQuestionnaire(selectedTemplate) &&
          selectedQuestionnaire &&
          organizationId &&
          practitionerId
        ) {
          const practitionerRoleReference = `PractitionerRole/${organizationId}-${practitionerId}`;
          const appointmentForTask = resolveOptionalAppointmentBasedOn(
            appointment.id,
          );
          const qPayload: QuestionnairePayload = {
            id: selectedQuestionnaire.id,
            title: selectedQuestionnaire.title,
            description: "",
            usageType: "postConsultation",
            questions: [],
          };
          for (const recipient of selectedRecipients) {
            const patientRef = resolvePatientReferenceForQuestionnaireTask(
              taskPatientReference,
              recipient,
            );
            if (!patientRef) {
              taskCreatesFail += 1;
              continue;
            }
            try {
              await sendQuestionnaireTasks({
                organizationId,
                practitionerRoleReference: practitionerRoleReference,
                patientReference: patientRef,
                ...(appointmentForTask
                  ? { appointmentReference: appointmentForTask }
                  : {}),
                questionnaires: [qPayload],
              });
              taskCreatesOk += 1;
            } catch (taskErr) {
              console.error(
                "Task de questionário (pós-WhatsApp) não criada:",
                taskErr,
              );
              taskCreatesFail += 1;
            }
          }
        }
        const count = selectedRecipients.length;
        toast.success(
          count === 1
            ? "Mensagem enviada com sucesso."
            : `Mensagens enviadas com sucesso para ${count} destinatários.`,
        );
        if (
          doesTemplateRequireQuestionnaire(selectedTemplate) &&
          selectedQuestionnaire
        ) {
          if (taskCreatesFail > 0 && taskCreatesOk === 0) {
            toast.warning(
              "Mensagem enviada, mas o questionário não pôde ser aberto no app do paciente para acompanhamento. Verifique o cadastro do destinatário.",
            );
          } else if (taskCreatesFail > 0) {
            toast.warning(
              "Mensagem enviada, mas o questionário não pôde ser registrado no app para parte dos destinatários.",
            );
          }
        }
        onClose();
        return;
      }

      toast.error(result.errorMessage);
    }, [
      appointment,
      onClose,
      organizationId,
      practitionerId,
      selectedRecipients,
      selectedQuestionnaire,
      selectedTemplate,
      sendFlow,
      sendQuestionnaireTasks,
      taskPatientReference,
    ]);

    const handleGoToTemplateStep = useCallback(() => {
      if (!canContinueToTemplate) return;
      setActiveView("template");
    }, [canContinueToTemplate]);

    const handleGoBack = useCallback(() => {
      if (skipPatientSelectionStep) return;
      setActiveView("patient");
    }, [skipPatientSelectionStep]);

    const dialogTitle = useMemo(() => {
      if (!purchasePackagesOnly && !hasFluxZapExtensionActive) {
        return "Enviar mensagem por WhatsApp";
      }
      if (showFranchisePurchaseOnlyView) {
        return "Contratar pacote de mensagens";
      }
      if (activeView === "patient") {
        return vetApp ? "Selecionar tutor" : "Selecionar paciente";
      }
      return "Enviar mensagem por WhatsApp";
    }, [
      activeView,
      hasFluxZapExtensionActive,
      purchasePackagesOnly,
      showFranchisePurchaseOnlyView,
      vetApp,
    ]);

    const selectedPatientsLabel = useMemo(() => {
      const labelPrefix = vetApp ? "tutor" : "paciente";
      if (selectedRecipients.length === 0)
        return `Nenhum ${labelPrefix} selecionado`;
      if (selectedRecipients.length === 1)
        return selectedRecipients[0].fullName;
      return `${selectedRecipients.length} ${labelPrefix}${
        selectedRecipients.length > 1 ? "s" : ""
      } selecionado${selectedRecipients.length > 1 ? "s" : ""}`;
    }, [selectedRecipients, vetApp]);

    const fluxZapNoticeBoxSx = {
      px: 1,
      py: 1.5,
      borderRadius: 2,
      bgcolor: alpha(theme.palette.info.main, 0.06),
      border: "1px dashed",
      borderColor: alpha(theme.palette.info.main, 0.35),
      width: "100%",
    } as const;

    const fluxZapInactiveNotice = (
      <Box sx={fluxZapNoticeBoxSx}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          <Box component="span" fontWeight={700} color="text.primary">
            Configure o FluxZap
          </Box>{" "}
          para enviar mensagens automaticamente por WhatsApp.{" "}
          <Link
            component={RouterLink}
            to="/extensions"
            state={{ highlightFluxZap: true }}
            onClick={handleClose}
            sx={fluxZapLinkSx}
          >
            Clique aqui
          </Link>{" "}
          para ativar.
        </Typography>
      </Box>
    );

    const fluxZapConfigureNotice = (
      <Box sx={fluxZapNoticeBoxSx}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          <Box component="span" fontWeight={700} color="text.primary">
            Configure o FluxZap
          </Box>{" "}
          para enviar mensagens automaticamente por WhatsApp.{" "}
          <Link
            component={RouterLink}
            to="/fluxzap"
            onClick={handleClose}
            sx={fluxZapLinkSx}
          >
            Clique aqui
          </Link>{" "}
          para configurar.
        </Typography>
      </Box>
    );

    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {activeView === "template" &&
              !skipPatientSelectionStep &&
              !showFranchisePurchaseOnlyView &&
              hasFluxZapExtensionActive && (
                <IconButton
                  aria-label="Voltar para seleção de paciente"
                  onClick={handleGoBack}
                  size="small"
                  sx={{ mr: 0.25 }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              )}
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {dialogTitle}
              </Typography>
              {activeView === "template" &&
                selectedRecipients.length > 0 &&
                hasFluxZapExtensionActive &&
                !showFranchisePurchaseOnlyView && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {vetApp ? "Tutor(es)" : "Paciente(s)"}:{" "}
                  {selectedPatientsLabel}
                </Typography>
              )}
            </Stack>
          </Stack>

          <IconButton
            aria-label="Fechar"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {isDialogContentLoading ? (
            <Stack spacing={2}>
              <CircularLoading />
            </Stack>
          ) : !purchasePackagesOnly && !hasFluxZapExtensionActive ? (
            fluxZapInactiveNotice
          ) : showFranchisePurchaseOnlyView ? (
            <Stack spacing={2}>
              {fluxZapConfigureNotice}
              <Typography variant="body1" fontWeight={600}>
                Você não tem mensagens disponíveis
              </Typography>
              <CrmSetupContractFluxmedPackagesSection
                onContractActivated={handleFranchisePurchaseDone}
              />
            </Stack>
          ) : (
            <Stack spacing={2}>
              {fluxZapConfigureNotice}

              {sendFlow.hasError && sendFlow.errorMessage && (
                <Alert severity="error">{sendFlow.errorMessage}</Alert>
              )}

              {sendFlow.isEndpointLoading && (
                <Alert severity="info">
                  Carregando configurações do WhatsApp (Endpoint)...
                </Alert>
              )}

              {franchiseGate.endpointLoadFailed && (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => franchiseGate.refetchEndpointCheck()}
                    >
                      Tentar novamente
                    </Button>
                  }
                >
                  Não foi possível verificar o canal WhatsApp da clínica. Tente
                  novamente.
                </Alert>
              )}

              {franchiseGate.franchiseError != null &&
                franchiseGate.requiresGate && (
                  <Alert
                    severity="warning"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => franchiseGate.refetchFranchise()}
                      >
                        Tentar novamente
                      </Button>
                    }
                  >
                    Não foi possível carregar o saldo de mensagens. Tente
                    novamente.
                  </Alert>
                )}

              {franchiseGate.hasLowRemainingMessages && (
                <Alert severity="warning">
                  Poucas mensagens restantes (
                  {franchiseGate.remainingMessages.toLocaleString("pt-BR")}).
                  Considere contratar um novo pacote antes de enviar em lote.
                </Alert>
              )}

              {selectedRecipientsExceedRemaining && (
                <Alert severity="error">
                  Você selecionou {selectedRecipients.length} destinatário(s),
                  mas só há{" "}
                  {franchiseGate.remainingMessages.toLocaleString("pt-BR")}{" "}
                  mensagem(ns) disponível(is). Reduza a seleção ou contrate mais
                  mensagens.
                </Alert>
              )}

              {!hasFluxzapRequester && (
                <Alert severity="warning">
                  Configure o solicitante do FluxZap: prop{" "}
                  <Typography component="span" variant="body2" fontWeight={700}>
                    requesterReference
                  </Typography>{" "}
                  ou a variável de ambiente{" "}
                  <Typography component="span" variant="body2" fontWeight={700}>
                    VITE_FLUXZAP_REQUESTER_REFERENCE
                  </Typography>{" "}
                  (ex.: PractitionerRole/...).
                </Alert>
              )}

              {selectedRecipients.length > 0 &&
                !allSelectedRecipientsAreSendable && (
                  <Alert severity="warning">
                    {vetApp
                      ? "Cada tutor selecionado precisa ter CPF cadastrado no RelatedPerson (identifier com OID de CPF) para envio via FluxZap."
                      : "Cada paciente selecionado precisa de CPF cadastrado para envio via FluxZap."}
                  </Alert>
                )}

              {activeView === "patient" ? (
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" color="text.secondary">
                      {selectedPatientsLabel}
                    </Typography>
                  </Stack>

                  {vetApp ? (
                    <TutorSelectionList
                      tutorsGrouped={tutorLookup.tutorsGrouped}
                      tutorsForSelection={tutorLookup.tutorsForSelection}
                      isLoading={tutorLookup.isLoading}
                      hasError={tutorLookup.hasError}
                      selectedTutors={selectedTutors}
                      onSelectedTutorsChange={setSelectedTutors}
                    />
                  ) : (
                    <PatientSelectionList
                      patients={
                        staticPatientListForSelection != null
                          ? staticPatientListForSelection
                          : patientLookup.patients
                      }
                      isSearching={
                        staticPatientListForSelection != null
                          ? false
                          : patientLookup.isSearching
                      }
                      hasSearchError={
                        staticPatientListForSelection != null
                          ? false
                          : patientLookup.hasSearchError
                      }
                      selectedPatients={selectedPatients}
                      onSelectedPatientsChange={setSelectedPatients}
                    />
                  )}
                </Stack>
              ) : (
                <>
                  {templatesForSelection.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nenhum modelo disponível.
                    </Typography>
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{ borderRadius: 2, overflow: "hidden" }}
                    >
                      <Box sx={communicationsRankTableToolbarSx}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.25}
                          alignItems={{ sm: "center" }}
                          justifyContent="space-between"
                        >
                          <Typography variant="subtitle2" fontWeight={700}>
                            Modelos disponíveis
                            <Typography
                              component="span"
                              variant="caption"
                              color="error.main"
                              fontWeight={700}
                              sx={{ ml: 0.25 }}
                            >
                              *
                            </Typography>
                          </Typography>
                          {!templateFromProps ? (
                            <TextField
                              fullWidth
                              size="small"
                              variant="filled"
                              label="Filtrar modelos"
                              placeholder="Busque por nome do modelo…"
                              value={templateSearchQuery}
                              onChange={(event) =>
                                setTemplateSearchQuery(event.target.value)
                              }
                              sx={communicationsRankFilledSearchFieldSx}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon
                                      fontSize="small"
                                      color="disabled"
                                    />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          ) : null}
                        </Stack>
                      </Box>

                      <Box
                        sx={{
                          height: TEMPLATE_MODELS_PANEL_HEIGHT_PX,
                          minHeight: TEMPLATE_MODELS_PANEL_HEIGHT_PX,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            px: 1.5,
                            py: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent:
                              filteredTemplatesForSelection.length === 0
                                ? "center"
                                : "flex-start",
                          }}
                        >
                          {filteredTemplatesForSelection.length === 0 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              textAlign="center"
                            >
                              Nenhum modelo corresponde à busca.
                            </Typography>
                          ) : (
                            <List
                              disablePadding
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                width: "100%",
                              }}
                            >
                              {paginatedTemplates.map((template) => {
                                const isSelected =
                                  selectedTemplateId === template.id;

                                return (
                                  <ListItem
                                    key={template.id}
                                    disablePadding
                                    sx={{ display: "block" }}
                                  >
                                    <ListItemButton
                                      onClick={() =>
                                        setSelectedTemplateId(template.id)
                                      }
                                      sx={{
                                        alignItems: "flex-start",
                                        borderRadius: 1.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        ...communicationsRankRowSx(isSelected),
                                      }}
                                    >
                                      <ListItemText
                                        primary={
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                          >
                                            <WhatsAppIcon
                                              sx={{
                                                color: "#25D366",
                                                fontSize: 18,
                                                flexShrink: 0,
                                              }}
                                            />
                                            <Typography
                                              variant="body2"
                                              fontWeight={500}
                                              color="text.primary"
                                              noWrap
                                            >
                                              {formatTemplateDisplayName(
                                                template.name,
                                                {
                                                  organizationPrefix:
                                                    organizationId,
                                                },
                                              )}
                                            </Typography>
                                          </Stack>
                                        }
                                        secondary={
                                          template.description ||
                                          template.preview ? (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                              }}
                                            >
                                              {template.description ??
                                                template.preview}
                                            </Typography>
                                          ) : null
                                        }
                                      />
                                    </ListItemButton>
                                  </ListItem>
                                );
                              })}
                            </List>
                          )}
                        </Box>

                        {!templateFromProps ? (
                          <Box
                            sx={{
                              flexShrink: 0,
                              minHeight: 52,
                              borderTop: "1px solid",
                              borderColor: "divider",
                              px: 1.5,
                              py: 0.5,
                            }}
                          >
                            <CommunicationsTablePagination
                              count={templateListCount}
                              page={templatePage}
                              rowsPerPage={templateRowsPerPage}
                              onPageChange={handleTemplatePageChange}
                              onRowsPerPageChange={
                                handleTemplateRowsPerPageChange
                              }
                            />
                          </Box>
                        ) : null}
                      </Box>
                    </Paper>
                  )}

                  {templateRequiresQuestionnaire && (
                    <Box>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ mb: 1 }}
                      >
                        Questionário
                        <Typography
                          component="span"
                          variant="caption"
                          color="error.main"
                          fontWeight={700}
                          sx={{ ml: 0.25 }}
                        >
                          *
                        </Typography>
                      </Typography>

                      {questionnairesErrorMessage && (
                        <Alert severity="warning" sx={{ mb: 1.5 }}>
                          {questionnairesErrorMessage}
                        </Alert>
                      )}

                      <Autocomplete<MessagingQuestionnaireOption>
                        options={questionnaires}
                        loading={isQuestionnairesLoading}
                        value={selectedQuestionnaire}
                        getOptionLabel={(option) => option.title}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        onChange={(_event, value) =>
                          setSelectedQuestionnaireId(value?.id ?? null)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Selecionar questionário"
                            placeholder="Escolha um questionário..."
                          />
                        )}
                      />
                    </Box>
                  )}
                </>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            color="inherit"
            disabled={sendFlow.isSending || sendFlow.isSendingMany}
          >
            Cancelar
          </Button>
          {(isDialogContentLoading ||
            showFranchisePurchaseOnlyView ||
            (!purchasePackagesOnly && !hasFluxZapExtensionActive)) ? null : activeView ===
          "patient" ? (
            <Button
              onClick={handleGoToTemplateStep}
              variant="contained"
              disabled={!canContinueToTemplate}
            >
              Continuar
            </Button>
          ) : (
            <>
              <Button
                onClick={handleOpenWhatsAppWeb}
                variant="outlined"
                color="inherit"
                disabled={!canOpenWhatsAppWeb || sendFlow.isSendingMany}
              >
                Abrir no WhatsApp Web
              </Button>
              <Button
                onClick={handleSend}
                variant="contained"
                color="primary"
                disabled={!canSend || sendFlow.isSendingMany}
                startIcon={<WhatsAppIcon sx={{ fontSize: 18 }} />}
              >
                {sendFlow.isSendingMany ? "Enviando..." : "Enviar por WhatsApp"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  },
);

SendWhatsAppMessageDialog.displayName = "SendWhatsAppMessageDialog";

export default SendWhatsAppMessageDialog;
